/**
 * Validation utilities for standardizing input validation across services
 */

export type ValidationResult = {
  valid: boolean;
  message?: string;
};

/**
 * Validates that a value is not undefined, null, or an empty string
 * @param value The value to validate
 * @param fieldName The name of the field being validated (for error messages)
 * @returns A ValidationResult object
 */
export function validateRequired(value: any, fieldName: string): ValidationResult {
  if (value === undefined || value === null || value === '') {
    return {
      valid: false,
      message: `${fieldName} is required`
    };
  }
  return { valid: true };
}

/**
 * Validates that a value is of the expected type
 * @param value The value to validate
 * @param expectedType The expected type ('string', 'number', 'boolean', 'object', 'array')
 * @param fieldName The name of the field being validated (for error messages)
 * @returns A ValidationResult object
 */
export function validateType(value: any, expectedType: string, fieldName: string): ValidationResult {
  // typeof null is object, so handle null separately
  if (value === null || value === undefined) {
    return { valid: true }; // null/undefined is valid for optional fields
  }
  
  // For arrays, check if it's an array
  if (expectedType === 'array' && !Array.isArray(value)) {
    return {
      valid: false,
      message: `${fieldName} must be an array`
    };
  }
  
  // For other types, use typeof
  if (typeof value !== expectedType && expectedType !== 'array') {
    return {
      valid: false,
      message: `${fieldName} must be of type ${expectedType}`
    };
  }
  
  return { valid: true };
}

/**
 * Validates that a value is a valid UUID
 * @param value The value to validate
 * @param fieldName The name of the field being validated (for error messages)
 * @returns A ValidationResult object
 */
export function validateUUID(value: string, fieldName: string): ValidationResult {
  // Handle optional UUIDs
  if (value === undefined || value === null) {
    return { valid: true };
  }
  
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(value)) {
    return {
      valid: false,
      message: `${fieldName} must be a valid UUID`
    };
  }
  return { valid: true };
}

/**
 * Validates that a string value does not exceed a maximum length
 * @param value The value to validate
 * @param maxLength The maximum allowed length
 * @param fieldName The name of the field being validated (for error messages)
 * @returns A ValidationResult object
 */
export function validateMaxLength(value: string, maxLength: number, fieldName: string): ValidationResult {
  // Handle optional strings
  if (value === undefined || value === null) {
    return { valid: true };
  }
  
  if (value.length > maxLength) {
    return {
      valid: false,
      message: `${fieldName} must be at most ${maxLength} characters`
    };
  }
  return { valid: true };
}

/**
 * Validates that a number is within a specified range
 * @param value The value to validate
 * @param min The minimum allowed value
 * @param max The maximum allowed value
 * @param fieldName The name of the field being validated (for error messages)
 * @returns A ValidationResult object
 */
export function validateRange(value: number, min: number, max: number, fieldName: string): ValidationResult {
  // Handle optional numbers
  if (value === undefined || value === null) {
    return { valid: true };
  }
  
  if (value < min || value > max) {
    return {
      valid: false,
      message: `${fieldName} must be between ${min} and ${max}`
    };
  }
  return { valid: true };
}

/**
 * Validates an object against a schema
 * @param obj The object to validate
 * @param schema The validation schema
 * @param objName The name of the object being validated (for error messages)
 * @returns An array of ValidationResult objects
 */
export function validateObject(
  obj: any, 
  schema: Record<string, { 
    required?: boolean, 
    type?: string, 
    maxLength?: number,
    min?: number,
    max?: number,
    isUUID?: boolean
  }>, 
  objName: string
): ValidationResult[] {
  const results: ValidationResult[] = [];
  
  // First validate the object itself
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return [{ valid: false, message: `${objName} must be an object` }];
  }
  
  // Then validate each field
  for (const [fieldName, rules] of Object.entries(schema)) {
    const value = obj[fieldName];
    
    // If required, validate that it exists
    if (rules.required) {
      const requiredResult = validateRequired(value, `${objName}.${fieldName}`);
      if (!requiredResult.valid) {
        results.push(requiredResult);
        continue; // Skip other validations if required validation failed
      }
    }
    
    // Skip other validations if value is undefined or null and not required
    if ((value === undefined || value === null) && !rules.required) {
      continue;
    }
    
    // If type is specified, validate type
    if (rules.type) {
      const typeResult = validateType(value, rules.type, `${objName}.${fieldName}`);
      if (!typeResult.valid) {
        results.push(typeResult);
      }
    }
    
    // If maxLength is specified and value is a string, validate maxLength
    if (rules.maxLength && typeof value === 'string') {
      const maxLengthResult = validateMaxLength(value, rules.maxLength, `${objName}.${fieldName}`);
      if (!maxLengthResult.valid) {
        results.push(maxLengthResult);
      }
    }
    
    // If min/max are specified and value is a number, validate range
    if ((rules.min !== undefined || rules.max !== undefined) && typeof value === 'number') {
      const min = rules.min !== undefined ? rules.min : Number.MIN_SAFE_INTEGER;
      const max = rules.max !== undefined ? rules.max : Number.MAX_SAFE_INTEGER;
      const rangeResult = validateRange(value, min, max, `${objName}.${fieldName}`);
      if (!rangeResult.valid) {
        results.push(rangeResult);
      }
    }
    
    // If isUUID is true and value is a string, validate UUID
    if (rules.isUUID && typeof value === 'string') {
      const uuidResult = validateUUID(value, `${objName}.${fieldName}`);
      if (!uuidResult.valid) {
        results.push(uuidResult);
      }
    }
  }
  
  return results;
} 